import { c as _c } from "react/compiler-runtime";
import ActionCard from '@precis-digital/kurama/ActionCard';
import AlertWarningIcon from '@precis-digital/kurama/icons/AlertWarningIcon';
import CancelIcon from '@precis-digital/kurama/icons/CancelIcon';
import CheckIcon from '@precis-digital/kurama/icons/CheckIcon';
import Chip from '@precis-digital/kurama/Chip';
import { CopilotActionCard } from '@precis-digital/kurama/MessageLayout';
import Grid from '@precis-digital/kurama/Grid';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { useState, type ReactElement } from 'react';
import { useCopilot } from 'shared/context/CopilotContext';
import { type ToolResult } from 'shared/copilot/toolResult';
import { buildCopilotConfirmedActionDecisionResult, buildCopilotConfirmedActionExecuteContext, confirmationItemDisplayLabel, confirmationItemTooltipTitle, getCopilotActionConfirmationChoiceMode, getCopilotActionConfirmationItemKey, getCopilotConfirmedActionExecutor, parseCopilotConfirmedActionResult, type CopilotActionConfirmation, type CopilotActionConfirmationItem } from 'shared/copilot/tools/confirmedAction';
import { useTranslation } from 'shared/translations';
import { buildHitlDecisionActions, useCopilotHitlDecision, type CopilotHitlDecision } from '../hitlDecision';
import ThemedChip from 'shared/components/Themed/Chip';
export interface ConfirmActionCardProps {
  args: Record<string, unknown>;
  toolCallId?: string;
  toolName: string;
  /** Pending (or later) HITL tool result content from AG-UI. */
  resultContent?: unknown;
}
const ConfirmationItemChip = t0 => {
  const $ = _c(14);
  const {
    item,
    isSelected,
    onClick
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = confirmationItemTooltipTitle(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tooltipTitle = t1;
  const t2 = item.iconUrl;
  let t3;
  if ($[2] !== item) {
    t3 = confirmationItemDisplayLabel(item);
    $[2] = item;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isSelected || $[5] !== item.iconUrl || $[6] !== onClick || $[7] !== t3) {
    t4 = <ThemedChip avatarSrc={t2} label={t3} isSelected={isSelected} onClick={onClick} />;
    $[4] = isSelected;
    $[5] = item.iconUrl;
    $[6] = onClick;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const chip = t4;
  if (tooltipTitle == null) {
    return chip;
  }
  let t5;
  if ($[9] !== chip) {
    t5 = <span>{chip}</span>;
    $[9] = chip;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5 || $[12] !== tooltipTitle) {
    t6 = <Tooltip kind="singleline" title={tooltipTitle}>{t5}</Tooltip>;
    $[11] = t5;
    $[12] = tooltipTitle;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};

/**
 * Reusable HITL card for any tool registered with `useCopilotConfirmedActionTool`.
 *
 * All copy comes from the pending tool result, so pages own the wording while
 * this card owns the Confirm/Cancel flow: on confirm it runs the tool's
 * registered executor and resumes the agent with the outcome, on cancel it
 * resumes with a rejection and changes nothing.
 */
const ConfirmActionCard = t0 => {
  const $ = _c(22);
  const {
    args,
    toolCallId,
    toolName,
    resultContent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot.toolCall.confirmAction"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const {
    sendToolResult
  } = useCopilot();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [selectedItemKeys, setSelectedItemKeys] = useState(t2);
  const [executionFailedMessage, setExecutionFailedMessage] = useState(null);
  const parsedResult = parseCopilotConfirmedActionResult(resultContent);
  const confirmation = parsedResult.confirmation;
  const {
    decision,
    isPending,
    startDecision,
    finishDecision
  } = useCopilotHitlDecision(parsedResult.status);
  let t3;
  if ($[2] !== args || $[3] !== finishDecision || $[4] !== sendToolResult || $[5] !== startDecision || $[6] !== t || $[7] !== toolCallId || $[8] !== toolName) {
    t3 = (next, activeConfirmation, t4) => {
      const executeArgs = t4 === undefined ? args : t4;
      const executor = next === "approved" ? getCopilotConfirmedActionExecutor(toolName) : undefined;
      if (!startDecision(next, {
        isAsync: next === "approved"
      })) {
        return;
      }
      const resume = outcome => {
        if (toolCallId == null || toolCallId.length === 0) {
          return;
        }
        sendToolResult({
          toolCallId,
          toolName,
          result: {
            ...buildCopilotConfirmedActionDecisionResult({
              decision: next,
              confirmation: activeConfirmation,
              outcome
            })
          }
        });
      };
      if (next === "rejected") {
        resume();
        return;
      }
      if (executor == null) {
        setExecutionFailedMessage(t("pageClosed"));
        finishDecision(null);
        resume({
          success: false,
          message: `The page that owns ${toolName} is no longer open, so the action did not run. Ask the user to try again from that page.`
        });
        return;
      }
      (async () => {
        let outcome_0;
        ;
        try {
          outcome_0 = await executor(executeArgs);
        } catch (t5) {
          const error = t5;
          outcome_0 = {
            success: false,
            message: `The action failed to run: ${error instanceof Error ? error.message : String(error)}`
          };
        }
        if (!outcome_0.success) {
          setExecutionFailedMessage(outcome_0.message ?? t("missingConfirmation"));
          finishDecision(null);
        } else {
          finishDecision("approved");
        }
        resume(outcome_0);
      })();
    };
    $[2] = args;
    $[3] = finishDecision;
    $[4] = sendToolResult;
    $[5] = startDecision;
    $[6] = t;
    $[7] = toolCallId;
    $[8] = toolName;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleDecision = t3;
  if (confirmation == null) {
    const isSuccess = parsedResult.success === true;
    let t4;
    if ($[10] !== isSuccess) {
      t4 = isSuccess ? <CheckIcon /> : <AlertWarningIcon />;
      $[10] = isSuccess;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return <CopilotActionCard icon={t4} label={isSuccess ? parsedResult.message ?? t("failedLabel") : t("failedLabel")} detail={!isSuccess ? <Typography variant="body3" color="secondary">{parsedResult.message ?? t("missingConfirmation")}</Typography> : undefined} />;
  }
  const choiceMode = getCopilotActionConfirmationChoiceMode(confirmation);
  const requiresChoice = choiceMode != null;
  const {
    selectedItems,
    executeArgs: executeArgs_0,
    approvalConfirmation
  } = buildCopilotConfirmedActionExecuteContext({
    args,
    confirmation,
    selectedItemKeys
  });
  const appliedItems = selectedItems.length > 0 ? selectedItems : confirmation.items;
  const hasSelection = selectedItems.length > 0;
  const handleItemClick = key => {
    if (choiceMode === "any") {
      setSelectedItemKeys(prev => prev.includes(key) ? prev.filter(selected => selected !== key) : [...prev, key]);
      return;
    }
    setSelectedItemKeys([key]);
  };
  if (decision === "rejected") {
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CancelIcon />;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return <Chip avatar={t4} label={confirmation.cancelledLabel} variant="semantic-neutral" />;
  }
  if (executionFailedMessage != null || parsedResult.status === "failed") {
    let t4;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AlertWarningIcon />;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== t) {
      t5 = t("failedLabel");
      $[14] = t;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return <CopilotActionCard icon={t4} label={t5} detail={<Typography variant="body3" color="secondary">{executionFailedMessage ?? parsedResult.message ?? t("missingConfirmation")}</Typography>} />;
  }
  if (decision === "approved") {
    let t4;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CheckIcon />;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return <CopilotActionCard icon={t4} label={confirmation.appliedLabel} detail={<StyledItems>{appliedItems.map(_temp)}</StyledItems>} />;
  }
  let t4;
  if ($[17] !== handleItemClick || $[18] !== isPending || $[19] !== requiresChoice || $[20] !== selectedItemKeys) {
    t4 = (item_0, index_0) => {
      const key_0 = getCopilotActionConfirmationItemKey(item_0, index_0);
      return <ConfirmationItemChip key={key_0} item={item_0} isSelected={requiresChoice && selectedItemKeys.includes(key_0)} onClick={requiresChoice && !isPending ? () => {
        handleItemClick(key_0);
      } : undefined} />;
    };
    $[17] = handleItemClick;
    $[18] = isPending;
    $[19] = requiresChoice;
    $[20] = selectedItemKeys;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return <ActionCard title={confirmation.title} variant={confirmation.isDestructive === true ? "semantic-destructive" : "semantic-info"} description={<StyledDescription><Typography variant="body2" color="secondary">{confirmation.description}</Typography><StyledItems>{confirmation.items.map(t4)}</StyledItems>{confirmation.warning != null && confirmation.warning.length > 0 ? <Typography variant="body3" color="secondary">{confirmation.warning}</Typography> : null}</StyledDescription>} actions={buildHitlDecisionActions({
    onDecision: next_0 => {
      if (next_0 === "approved" && requiresChoice && !hasSelection) {
        return;
      }
      handleDecision(next_0, next_0 === "rejected" ? confirmation : approvalConfirmation, executeArgs_0);
    },
    approveLabel: confirmation.confirmLabel,
    rejectLabel: confirmation.cancelLabel ?? t("cancel"),
    isDestructive: confirmation.isDestructive,
    disabled: isPending,
    approveDisabled: requiresChoice && !hasSelection
  })} />;
};
const StyledDescription = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  alignItems: 'flex-start'
}));
const StyledItems = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  alignItems: 'center'
}));
export default ConfirmActionCard;
function _temp(item, index) {
  return <ConfirmationItemChip key={getCopilotActionConfirmationItemKey(item, index)} item={item} />;
}